import type * as THREE from 'three/webgpu';
import type { MeasureEntityRef } from '../types/measure-types';
import type { MeasureCircleDescriptor } from './measureArcCircleReferencePoints';

/**
 * 圆弧/圆与非圆几何体混合测量上下文。
 */
export interface MeasureArcCircleMixedContext {
    /** 圆/圆弧侧测量实体。 */
    circleEntity: MeasureEntityRef;
    /** 非圆/圆弧侧测量实体。 */
    otherEntity: MeasureEntityRef;
    /** 圆/圆弧侧是否为先选实体。 */
    circleSideIsFirst: boolean;
    /** 圆/圆弧侧几何描述符。 */
    circleDescriptor: MeasureCircleDescriptor;
}

/**
 * 判断是否为双圆（双方均可解析圆描述符）测量场景。
 *
 * @param circleA 先选实体圆描述符。
 * @param circleB 后选实体圆描述符。
 * @returns 是否为双圆场景。
 */
export function isDualCircleMeasurementContext(
    circleA: MeasureCircleDescriptor | null,
    circleB: MeasureCircleDescriptor | null,
): boolean {
    return circleA !== null && circleB !== null;
}

/**
 * 尝试构建圆弧/圆混合测量上下文。
 *
 * @param firstEntity 先选实体。
 * @param secondEntity 后选实体。
 * @param circleA 先选实体圆描述符。
 * @param circleB 后选实体圆描述符。
 * @returns 混合上下文；不适用时返回 null。
 */
export function tryBuildArcCircleMixedContext(
    firstEntity: MeasureEntityRef,
    secondEntity: MeasureEntityRef,
    circleA: MeasureCircleDescriptor | null,
    circleB: MeasureCircleDescriptor | null,
): MeasureArcCircleMixedContext | null {
    if (isDualCircleMeasurementContext(circleA, circleB)) {
        return null;
    }

    if (circleA) {
        return {
            circleEntity: firstEntity,
            otherEntity: secondEntity,
            circleSideIsFirst: true,
            circleDescriptor: circleA,
        };
    }

    if (circleB) {
        return {
            circleEntity: secondEntity,
            otherEntity: firstEntity,
            circleSideIsFirst: false,
            circleDescriptor: circleB,
        };
    }

    return null;
}

/**
 * 解析混合测量中非圆/圆弧侧锚点 A（圆心在该几何体上的最近点）。
 *
 * @param context 混合测量上下文。
 * @param resolveClosestPointOnEntity 最近点求解回调。
 * @returns 锚点 A；无法求解时返回 null。
 */
export function resolveMixedMeasurementAnchorPoint(
    context: MeasureArcCircleMixedContext,
    resolveClosestPointOnEntity: (
        entityRef: MeasureEntityRef,
        worldPoint: THREE.Vector3,
    ) => THREE.Vector3 | null,
): THREE.Vector3 | null {
    return resolveClosestPointOnEntity(
        context.otherEntity,
        context.circleDescriptor.center,
    );
}

/**
 * 判断混合测量锚点 A 是否落在先选实体侧。
 *
 * @param context 混合测量上下文。
 * @returns 锚点是否对应 `from` 侧。
 */
export function isMixedMeasurementAnchorOnFirstEntity(
    context: MeasureArcCircleMixedContext,
): boolean {
    return !context.circleSideIsFirst;
}
